"use client";

import {
  motion,
  type MotionValue,
  useMotionValueEvent,
  useScroll,
} from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import React, { useRef } from "react";

import { formatCompactCount, NETWORK_STATS } from "@/config/network-stats";
import { cn } from "@/lib/utils";

interface Step {
  id: number;
  title: string;
  bullets: string[];
  cta: {
    text: string;
    href: string;
  };
  media: React.ReactNode;
}

// Icons used by media components
function LocationIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 9.75C10.2426 9.75 11.25 8.74264 11.25 7.5C11.25 6.25736 10.2426 5.25 9 5.25C7.75736 5.25 6.75 6.25736 6.75 7.5C6.75 8.74264 7.75736 9.75 9 9.75Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 16.5C12 13.5 15 10.8137 15 7.5C15 4.18629 12.3137 1.5 9 1.5C5.68629 1.5 3 4.18629 3 7.5C3 10.8137 6 13.5 9 16.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TagIcon({ className }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M15.75 9L9 15.75L2.25 9L9 2.25L15.75 9Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlusCircleIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 5V11M5 8H11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-marketing-text"
    >
      <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.5" />
      <ellipse
        cx="9"
        cy="9"
        rx="3"
        ry="7.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path d="M2 9H16" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      width="24"
      height="16"
      viewBox="0 0 24 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 8H23M23 8L16 1M23 8L16 15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Deterministic heatmap pattern to avoid hydration issues
const HEATMAP_PATTERN = [
  [0, 1, 2, 0, 3, 4, 1, 0, 2, 1, 4, 0, 1, 1, 3, 0, 3, 4, 1, 2, 0, 2, 1],
  [4, 3, 3, 2, 1, 0, 1, 0, 1, 0, 2, 1, 2, 0, 0, 3, 1, 2, 2, 2, 4, 1, 1],
  [2, 1, 0, 0, 4, 3, 3, 1, 2, 2, 2, 2, 2, 2, 2, 3, 0, 3, 3, 1, 3, 0, 1],
  [0, 4, 1, 2, 0, 1, 0, 2, 1, 3, 2, 4, 4, 1, 0, 0, 2, 0, 0, 1, 1, 3, 0],
  [1, 1, 0, 2, 3, 0, 3, 1, 0, 4, 2, 1, 2, 1, 1, 1, 3, 0, 0, 2, 2, 0, 4],
  [0, 0, 2, 2, 1, 3, 0, 0, 1, 2, 0, 3, 1, 0, 2, 1, 2, 4, 1, 3, 0, 1, 0],
];

function ActivityHeatmap() {
  const colors = [
    "var(--color-marketing-cream-light)",
    "var(--color-blue-100, #dbeafe)",
    "var(--color-blue-300, #93c5fd)",
    "var(--color-blue-500, #3b82f6)",
    "var(--color-marketing-blue)",
  ];

  return (
    <div className="flex flex-col gap-0.5">
      {HEATMAP_PATTERN.map((row, rowIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`row-${rowIndex}`} className="flex gap-0.5">
          {row.map((colorIndex, colIndex) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`cell-${rowIndex}-${colIndex}`}
              className="border-marketing-border h-[15px] w-[15px] rounded-[2px] border-[0.5px]"
              style={{ backgroundColor: colors[colorIndex] }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// Step 1 Media: Interview phone mockup with participant cards
function Step1Media() {
  return (
    <div className="relative h-full w-full overflow-visible">
      {/* Main interview phone - center, maintaining original aspect ratio */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-black/10 shadow-lg">
        <Image
          src="/marketing/steps/interview-phone.png"
          alt="Interview call"
          width={227}
          height={486}
          className="object-contain"
        />
      </div>

      {/* Participant 1 - top right (Miami) with tags - overlaps phone */}
      <div className="absolute top-4 right-12">
        <div className="relative mb-2 h-[123px] w-[107px] overflow-hidden rounded-[10px] shadow-sm">
          <Image
            src="/marketing/steps/participant-1.png"
            alt="Expert"
            fill
            className="object-cover"
          />
          <div className="absolute top-2 left-2 h-[9px] w-[9px] rounded-full bg-red-500" />
        </div>
        <div className="flex flex-col items-start gap-2">
          <div className="border-marketing-border bg-marketing-cream text-marketing-text flex items-center gap-1.5 rounded-full border px-2 py-1.5 text-sm font-medium shadow-sm">
            <LocationIcon />
            Miami, Florida
          </div>
          <div className="border-marketing-badge-green-border bg-marketing-badge-green-bg text-marketing-accent-green-dark flex items-center gap-1.5 rounded-full border px-2 py-1.5 text-sm font-medium shadow-sm">
            <TagIcon className="text-marketing-accent-green-dark" />
            Uses React daily
          </div>
        </div>
      </div>

      {/* Participant 2 - left side with tags */}
      <div className="absolute bottom-20 left-0">
        <div className="relative mb-2 h-[187px] w-[163px] overflow-hidden rounded-[10px] shadow-sm">
          <Image
            src="/marketing/steps/participant-3.png"
            alt="Expert"
            fill
            className="object-cover"
          />
          <div className="absolute top-2 left-2 h-[9px] w-[9px] rounded-full bg-red-500" />
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="border-marketing-border bg-marketing-cream text-marketing-text flex items-center gap-1.5 rounded-full border px-2 py-1.5 text-sm font-medium shadow-sm">
            <LocationIcon />
            Toronto, Canada
          </div>
          <div className="border-marketing-badge-red-border bg-marketing-badge-red-bg text-marketing-accent-red-dark flex items-center gap-1.5 rounded-full border px-2 py-1.5 text-sm font-medium shadow-sm">
            <TagIcon className="text-marketing-accent-red-dark" />
            Prefers backend
          </div>
        </div>
      </div>

      {/* Participant 3 - bottom right with tags */}
      <div className="absolute right-0 bottom-4">
        <div className="relative mb-2 h-[100px] w-[87px] overflow-hidden rounded-[10px] shadow-sm">
          <Image
            src="/marketing/steps/participant-2.png"
            alt="Expert"
            fill
            className="object-cover"
          />
          <div className="absolute top-2 left-2 h-[9px] w-[9px] rounded-full bg-red-500" />
        </div>
        <div className="flex flex-col items-start gap-2">
          <div className="border-marketing-border bg-marketing-cream text-marketing-text flex items-center gap-1.5 rounded-full border px-2 py-1.5 text-sm font-medium shadow-sm">
            <LocationIcon />
            Atlanta, Georgia
          </div>
          <div className="border-marketing-badge-lime-border bg-marketing-badge-lime-bg text-marketing-accent-green-success flex items-center gap-1.5 rounded-full border px-2 py-1.5 text-sm font-medium shadow-sm">
            <TagIcon className="text-marketing-accent-green-success" />
            Full-stack experience
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 2 Media: AI conversation with video
function Step2Media() {
  return (
    <div className="flex h-full w-full flex-col items-center gap-4 overflow-visible">
      {/* Video thumbnail */}
      <div className="flex flex-col items-center">
        <div className="relative h-[200px] w-[220px] overflow-hidden rounded-[16px]">
          <Image
            src="/marketing/steps/step2-person.png"
            alt="Interview expert"
            fill
            className="object-cover"
          />
        </div>
      </div>

      {/* Chat bubbles - vertical stack */}
      <div className="flex w-full flex-col gap-3">
        {/* AI question 1 */}
        <div className="border-marketing-border bg-marketing-card-blue flex w-fit items-center gap-3 rounded-[10px] border px-4 py-3">
          <Image
            src="/marketing/steps/chat-waveform.svg"
            alt=""
            width={22}
            height={19}
            className="shrink-0"
          />
          <p className="text-marketing-blue-dark text-base font-medium">
            What tools do you use for CI/CD?
          </p>
        </div>

        {/* User response - right aligned */}
        <div className="border-marketing-border bg-marketing-cream ml-auto max-w-[280px] rounded-[10px] border px-4 py-3">
          <p className="text-marketing-text text-base font-medium">
            GitHub Actions mostly. We migrated from Jenkins last year and
            it&apos;s been much smoother.
          </p>
        </div>

        {/* AI follow-up */}
        <div className="border-marketing-border bg-marketing-card-blue flex w-fit max-w-[300px] items-start gap-3 rounded-[10px] border px-4 py-3">
          <Image
            src="/marketing/steps/chat-waveform-alt.svg"
            alt=""
            width={22}
            height={23}
            className="mt-0.5 shrink-0"
          />
          <p className="text-marketing-blue-dark text-base font-medium">
            Interesting; what made Jenkins frustrating? Was it the setup
            complexity or something else?
          </p>
        </div>
      </div>
    </div>
  );
}

// Step 3 Media: Expert profile with activity heatmap and skills
function Step3Media() {
  return (
    <div className="relative h-full w-full overflow-visible">
      {/* Profile card - top left */}
      <div className="border-marketing-border bg-marketing-cream absolute top-0 -left-20 rounded-[20px] border p-6 shadow-sm">
        <div className="flex gap-5">
          {/* Avatar */}
          <div className="border-marketing-border relative h-[100px] w-[100px] shrink-0 overflow-hidden rounded-full border">
            <Image
              src="/marketing/steps/profile-maya.png"
              alt="Maya Donaldson"
              fill
              className="object-cover"
            />
          </div>
          {/* Info */}
          <div className="flex flex-col gap-2.5 py-2.5">
            <div>
              <p className="text-xl font-medium text-black">Maya Donaldson</p>
              <p className="text-sm text-black">@mayadonaldson</p>
            </div>
            <button className="border-marketing-border bg-marketing-cream text-marketing-text-muted flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-sm font-medium">
              <PlusCircleIcon />
              Add to Shortlist
            </button>
          </div>
        </div>
        {/* Location info */}
        <div className="mt-2 flex flex-col">
          <div className="flex items-center gap-1.5 py-1.5">
            <GlobeIcon />
            <span className="text-marketing-text text-sm font-medium">
              United States
            </span>
          </div>
          <div className="flex items-center gap-1.5 py-1.5">
            <LocationIcon />
            <span className="text-marketing-text text-sm font-medium">
              Atlanta, Georgia
            </span>
          </div>
        </div>
      </div>

      {/* Activity heatmap card - right side, extending to card edge */}
      <div className="border-marketing-border bg-marketing-cream absolute top-[144px] -right-12 rounded-l-[20px] border border-r-0 px-6 pt-6 pb-8 shadow-sm">
        <p className="text-marketing-text mb-2.5 text-lg font-medium">
          126 opportunities in the last year
        </p>
        <div className="text-marketing-text-secondary mb-2.5 flex gap-[43px] text-sm font-medium">
          <span>Jan</span>
          <span>Feb</span>
          <span>Mar</span>
          <span>Apr</span>
          <span>May</span>
          <span>Jun</span>
        </div>
        <ActivityHeatmap />
      </div>

      {/* Top Skills card - bottom */}
      <div className="border-marketing-border bg-marketing-cream absolute bottom-0 left-[67px] rounded-[20px] border p-6 shadow-sm">
        <p className="text-marketing-text mb-2.5 text-lg font-medium">
          Top Skills
        </p>
        <div className="flex flex-wrap gap-1.5" style={{ width: 344 }}>
          <span className="bg-marketing-accent-pink rounded-full px-3 py-2 text-sm font-medium text-white">
            Software Engineering
          </span>
          <span className="bg-marketing-accent-red rounded-full px-3 py-2 text-sm font-medium text-white">
            Linux
          </span>
          <span className="border-marketing-border text-marketing-text rounded-full border bg-white px-3 py-2 text-sm font-medium">
            Python
          </span>
          <span className="bg-marketing-accent-green text-marketing-text rounded-full px-3 py-2 text-sm font-medium">
            Machine Learning Engineer
          </span>
        </div>
      </div>
    </div>
  );
}

// Steps data (must be after media component definitions)
const steps: Step[] = [
  {
    id: 1,
    title: "Recruit experts in hours",
    bullets: [
      "Launch projects in hours, not weeks",
      `Access ${formatCompactCount(NETWORK_STATS.registered)} registered experts instantly`,
      "AI-led screening that detect fraud",
      "Find niche audiences others can't",
    ],
    cta: {
      text: "Talk to our team",
      href: "/contact?inquiry=research",
    },
    media: <Step1Media />,
  },
  {
    id: 2,
    title: "AI-led screening that builds context and catches fraud",
    bullets: [
      "Open-ended, AI-led interviews replace human screening",
      "Expert profiles evolve overtime to enhance future matchmaking",
      "Real-time fraud detection flags inconsistencies",
      "Fewer misaligned recruits with AI-powered matchmaking",
    ],
    cta: {
      text: "Take demo interview",
      href: "/demo",
    },
    media: <Step2Media />,
  },
  {
    id: 3,
    title: "Expert profiles that evolve with every interaction",
    bullets: [
      "Context accumulates across screening interviews,\nopportunity metadata, and platform behavior",
      "Richer profiles means faster, more precise matching",
      "Continuous evaluation tracks expert quality",
      "Fully managed expert agreements and payouts",
    ],
    cta: {
      text: "See what we can reach",
      href: "/contact?inquiry=recruitment",
    },
    media: <Step3Media />,
  },
];

export function StepsScroll() {
  const [activeStep, setActiveStep] = React.useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    const stepIndex = Math.min(
      Math.floor(latest * steps.length),
      steps.length - 1,
    );
    setActiveStep(stepIndex);
  });

  return (
    <section className="py-20" ref={ref}>
      <div className="mx-auto max-w-[1466px] px-4">
        {/* Main card container */}
        <div
          className="border-marketing-border bg-marketing-cream-light relative rounded-[20px] border"
          style={{ minHeight: `${steps.length * 100}vh` }}
        >
          {/* Sticky content wrapper */}
          <div className="sticky top-0 flex h-screen items-center overflow-hidden rounded-[20px]">
            <div className="flex w-full gap-4 p-4 sm:gap-6 sm:p-6 md:gap-8 md:p-8 lg:p-12">
              {/* Left: Number indicators */}
              <NumberIndicators
                activeStep={activeStep}
                totalSteps={3}
                scrollYProgress={scrollYProgress}
              />

              {/* Middle: Content */}
              <div className="flex-1">
                <StepContent step={steps[activeStep]!} />
              </div>

              {/* Right: Media area - no background, same as content */}
              <div className="hidden w-[475px] shrink-0 lg:block">
                <div className="relative h-[486px]">
                  {steps[activeStep]?.media}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function NumberIndicators({
  activeStep,
  totalSteps,
  scrollYProgress: _scrollYProgress,
}: {
  activeStep: number;
  totalSteps: number;
  scrollYProgress: MotionValue<number>;
}) {
  return (
    <div className="relative flex flex-col items-center gap-0 py-8">
      {/* Two vertical lines */}
      <div className="bg-marketing-border absolute top-0 left-4 h-full w-px" />
      <div className="bg-marketing-border absolute top-0 left-6 h-full w-px" />

      {/* Numbers */}
      <div className="relative z-10 flex h-full flex-col justify-between py-4">
        {Array.from({ length: totalSteps }).map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`step-num-${index}`} className="flex items-center gap-8">
            <span
              className={cn(
                "font-mono text-base tracking-[-0.32px] transition-colors duration-300",
                index <= activeStep
                  ? "text-marketing-blue"
                  : "text-marketing-border",
              )}
            >
              {index + 1}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepContent({ step }: { step: Step }) {
  return (
    <motion.div
      key={step.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col gap-12"
    >
      {/* Icon + Title */}
      <div className="flex items-start gap-4">
        <StepIcon stepId={step.id} />
        <h3 className="text-marketing-text max-w-[400px] text-2xl leading-[1.3] font-medium tracking-[-0.36px] md:text-3xl lg:text-4xl">
          {step.title}
        </h3>
      </div>

      {/* Bullets */}
      <div className="flex flex-col gap-3">
        {step.bullets.map((bullet) => (
          <div key={bullet} className="flex items-start gap-2.5">
            <div className="bg-marketing-text-secondary mt-2.5 h-2 w-2 shrink-0 rounded-sm" />
            <p className="text-marketing-text text-base leading-[1.4] font-medium whitespace-pre-line md:text-lg lg:text-xl">
              {bullet}
            </p>
          </div>
        ))}
      </div>

      {/* CTA */}
      <Link
        href={step.cta.href}
        className="text-marketing-blue inline-flex items-center gap-2 text-base"
      >
        <ArrowIcon />
        <span>{step.cta.text}</span>
      </Link>
    </motion.div>
  );
}

function StepIcon({ stepId }: { stepId: number }) {
  if (stepId === 1) {
    return (
      <Image
        src="/marketing/steps/speech-icon.svg"
        alt=""
        width={29}
        height={25}
        className="shrink-0"
      />
    );
  }
  if (stepId === 2) {
    return (
      <Image
        src="/marketing/steps/waveform-icon.svg"
        alt=""
        width={29}
        height={25}
        className="shrink-0"
      />
    );
  }
  if (stepId === 3) {
    return (
      <Image
        src="/marketing/steps/profile-icon.svg"
        alt=""
        width={29}
        height={25}
        className="shrink-0"
      />
    );
  }
  return null;
}
